import React, {
  useMemo,
  useRef,
} from 'react'

import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'

import { Button } from '@termly-web/react-components'

import URLBuilder from 'lib/URLBuilder'

import ExternalLink from '../ExternalLink'
import SmartLink from '../SmartLink'

import Locale from './locale'

import Styles from './styles.scss'

const GLOBAL_LINKS = [
  {
    url: 'https://termly.io/our-privacy-policy/',
    text: Locale.ourPrivacyPolicy,
  },
  {
    url: 'https://termly.io/our-terms-of-use/',
    text: Locale.ourTOU,
  },
  {
    url: 'https://termly.io/our-disclaimer/',
    text: Locale.ourDisclaimer,
  },
  {
    url: 'https://termly.io/our-cookie-policy/',
    text: Locale.ourCookiePolicy,
  },
]

/*
 * The DSAR form shares the dashboard's origin but is served by a separate
 * application, so these links must bypass <SmartLink />: it treats any
 * same-origin URL as an in-app route, which lands on "page not found".
 */
const TERMLY_DSAR_FORM_LINK = 'https://app.termly.io/notify/05f55d8a-a00b-45d4-8053-55f6d8637f46'

const SIGNED_IN_LINKS = [
  {
    url: URLBuilder.supportURL(),
    text: Locale.support,
  },
  {
    LinkComponent: ExternalLink,
    url: TERMLY_DSAR_FORM_LINK,
    text: Locale.limitUse,
  },
  {
    LinkComponent: ExternalLink,
    url: TERMLY_DSAR_FORM_LINK,
    text: Locale.doNotSell,
  },
]


export default function Footer({ isGuest = false }) {
  const loggedMessage = useRef()

  const filteredLinks = useMemo(() => {
    return filterLinks(isGuest)
  }, [isGuest])

  return (
    <footer className={ Styles.root }>
      <ul className={ Styles.links }>
        {
          filteredLinks.map(({ LinkComponent = SmartLink, url, text }) => (
            <li key={ text.id }>
              <LinkComponent
                className={ Styles.link }
                showIcon={ false }
                url={ url }
              >
                <FormattedMessage
                  { ...text }
                />
              </LinkComponent>
            </li>
          ))
        }
        {
          renderConsentPreferencesLink(loggedMessage)
        }
      </ul>
    </footer>
  )
}

Footer.propTypes = {
  isGuest: PropTypes.bool,
}

function filterLinks(isGuest) {
  if ( isGuest ) {
    return GLOBAL_LINKS
  }

  return [...GLOBAL_LINKS, ...SIGNED_IN_LINKS]
}

function renderConsentPreferencesLink(loggedMessage) {
  if ( !window.displayPreferenceModal ) {
    if ( !loggedMessage.current ) {
      console.debug('Not rendering the Consent Preferences footer link: window.displayPreferenceModal() was not found')
      loggedMessage.current = true
    }

    return null
  }

  const className = `${ Styles.link } t-displayPreferencesLink`

  return (
    <li>
      <Button
        className={ className }
        handleClick={ window.displayPreferenceModal }
        type="text"
      >
        <FormattedMessage
          { ...Locale.consentPreferences }
        />
      </Button>
    </li>
  )
}
